/**
 * Permission Templates manager (Super Admin only).
 *
 * Saved, reusable permission bundles that get LINKED to users. Because the
 * link is live, editing a template instantly updates every linked user's
 * effective permissions (resolved server-side on each request).
 *
 * Rendered inside a Dialog from the Admin → Users page. Consumes the shared
 * ``permGroups`` catalog (from GET /users/permissions) so the checkbox list
 * always matches the backend source of truth.
 */
import React, { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { PermissionMatrixEditor } from '@/components/admin/PermissionMatrixEditor';
import { PermissionPreview } from '@/components/admin/PermissionPreview';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { extractError } from '@/lib/errors';
import { HoverCard, HoverCardTrigger, HoverCardContent } from '@/components/ui/hover-card';
import { Plus, Pencil, Trash2, ArrowLeft, Users, Eye } from 'lucide-react';

export function PermissionTemplatesManager({
  open,
  onOpenChange,
  companyId,
  companyName,
  permGroups = [],
  onChanged,   // () => void — fired after any create/update/delete so callers can refresh
}) {
  // view: 'list' | 'editor'
  const [view, setView] = useState('list');
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null);     // template being edited (null = new)
  const [name, setName] = useState('');
  const [perms, setPerms] = useState([]);
  const [saving, setSaving] = useState(false);
  const [delTarget, setDelTarget] = useState(null);
  // Permission Preview — a read-only "what can this see?" view. ``previewTpl``
  // drives the standalone preview opened from the list; inside the editor the
  // preview now sits side-by-side with the matrix (always visible).
  const [previewTpl, setPreviewTpl] = useState(null);

  const load = async () => {
    if (!companyId) { setTemplates([]); return; }
    setLoading(true);
    try {
      const { data } = await api.get('/permission-templates', { params: { company_id: companyId } });
      setTemplates(Array.isArray(data) ? data : []);
    } catch (e) {
      toast.error(extractError(e, 'Failed to load templates'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) { setView('list'); setPreviewTpl(null); load(); }
    /* eslint-disable-next-line */
  }, [open, companyId]);

  const openEditor = (tpl) => {
    setEditing(tpl);
    setName(tpl?.name || '');
    setPerms(tpl?.permissions ? [...tpl.permissions] : []);
    setView('editor');
  };

  const openPreview = (tpl) => {
    setPreviewTpl(tpl);
    setView('preview');
  };

  const save = async () => {
    const nm = (name || '').trim();
    if (!nm) { toast.error('Template name is required'); return; }
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/permission-templates/${editing.id}`, { name: nm, permissions: perms });
        toast.success(`Template "${nm}" updated — linked users refreshed`);
      } else {
        await api.post('/permission-templates', { name: nm, permissions: perms, company_id: companyId });
        toast.success(`Template "${nm}" created`);
      }
      setView('list');
      await load();
      if (onChanged) onChanged();
    } catch (e) {
      toast.error(extractError(e, 'Failed to save template'));
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!delTarget) return;
    try {
      await api.delete(`/permission-templates/${delTarget.id}`);
      toast.success(`Template "${delTarget.name}" deleted — linked users keep their current access`);
      setDelTarget(null);
      await load();
      if (onChanged) onChanged();
    } catch (e) {
      toast.error(extractError(e, 'Failed to delete template'));
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-6xl max-h-[92vh] overflow-y-auto" data-testid="permission-templates-dialog">
        <DialogHeader>
          <DialogTitle>
            {view === 'list'
              ? 'Permission Templates'
              : view === 'preview'
                ? 'Access Preview'
                : editing ? 'Edit Template' : 'New Template'}
          </DialogTitle>
          <DialogDescription>
            {companyName ? `Company: ${companyName}. ` : ''}
            Saved permission bundles you can link to users. Editing a template updates every linked user instantly.
          </DialogDescription>
        </DialogHeader>

        {view === 'list' && (
          <div className="space-y-3">
            <div className="flex justify-end">
              <Button
                className="luxury-button h-9"
                onClick={() => openEditor(null)}
                disabled={!companyId}
                data-testid="template-new-button"
              >
                <Plus size={14} className="mr-1.5" /> New Template
              </Button>
            </div>
            {!companyId && (
              <div className="text-sm text-slate-500 py-6 text-center">
                Select a company first to manage its templates.
              </div>
            )}
            {companyId && (
              <div className="rounded-lg border border-[var(--divider)] overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="text-left px-3 py-2 font-medium text-slate-600">Template</th>
                      <th className="text-left px-3 py-2 font-medium text-slate-600">Permissions</th>
                      <th className="text-left px-3 py-2 font-medium text-slate-600">Linked</th>
                      <th className="text-right px-3 py-2 font-medium text-slate-600">Actions</th>
                    </tr>
                  </thead>
                  <tbody data-testid="templates-table-body">
                    {loading && (
                      <tr><td colSpan={4} className="text-center py-6 text-slate-400">Loading…</td></tr>
                    )}
                    {!loading && templates.length === 0 && (
                      <tr><td colSpan={4} className="text-center py-6 text-slate-500" data-testid="templates-empty">
                        No templates yet. Click "New Template" to create one.
                      </td></tr>
                    )}
                    {!loading && templates.map((t) => (
                      <tr key={t.id} className="border-t border-[var(--divider)]" data-testid={`template-row-${t.id}`}>
                        <td className="px-3 py-2 font-medium text-[var(--ink-navy)]">{t.name}</td>
                        <td className="px-3 py-2 text-slate-600">
                          <Badge variant="outline" className="bg-slate-50 text-slate-700 border-slate-200 text-[10px]">
                            {(t.permissions || []).length} perms
                          </Badge>
                        </td>
                        <td className="px-3 py-2">
                          {(t.linked_user_count ?? 0) > 0 ? (
                            <HoverCard openDelay={80} closeDelay={80}>
                              <HoverCardTrigger asChild>
                                <span
                                  className="inline-flex items-center gap-1 text-xs text-slate-600 cursor-help rounded px-1 py-0.5 underline decoration-dotted decoration-slate-300 underline-offset-2 hover:bg-slate-100 hover:text-[var(--ink-navy)] transition-colors"
                                  tabIndex={0}
                                  role="button"
                                  aria-label={`${t.linked_user_count} linked user(s) — hover to see names`}
                                  data-testid={`template-linked-${t.id}`}
                                >
                                  <Users size={12} /> {t.linked_user_count ?? 0}
                                </span>
                              </HoverCardTrigger>
                              <HoverCardContent
                                align="start"
                                className="w-64 p-0 overflow-hidden"
                                data-testid={`template-linked-popover-${t.id}`}
                              >
                                <div className="px-3 py-2 border-b border-[var(--divider)] bg-slate-50">
                                  <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500 flex items-center gap-1.5">
                                    <Users size={12} /> Linked Users · {t.linked_user_count ?? 0}
                                  </div>
                                </div>
                                <div className="max-h-56 overflow-y-auto py-1">
                                  {(t.linked_users || []).map((u) => (
                                    <div
                                      key={u.id}
                                      className="px-3 py-1.5 hover:bg-slate-50"
                                      data-testid={`template-linked-user-${u.id}`}
                                    >
                                      <div className="text-xs font-medium text-[var(--ink-navy)] truncate">
                                        {u.name || u.email || 'Unnamed user'}
                                      </div>
                                      {u.email && (
                                        <div className="text-[11px] text-slate-500 truncate">{u.email}</div>
                                      )}
                                    </div>
                                  ))}
                                  {(!t.linked_users || t.linked_users.length === 0) && (
                                    <div className="px-3 py-2 text-[11px] text-slate-400">
                                      Names unavailable — refresh the list.
                                    </div>
                                  )}
                                </div>
                              </HoverCardContent>
                            </HoverCard>
                          ) : (
                            <span
                              className="inline-flex items-center gap-1 text-xs text-slate-400"
                              data-testid={`template-linked-${t.id}`}
                            >
                              <Users size={12} /> 0
                            </span>
                          )}
                        </td>
                        <td className="px-3 py-2 text-right">
                          <div className="flex justify-end gap-1">
                            <Button variant="ghost" size="sm" className="h-7 px-2" onClick={() => openPreview(t)} data-testid={`template-preview-${t.id}`} title="Preview access">
                              <Eye size={13} />
                            </Button>
                            <Button variant="ghost" size="sm" className="h-7 px-2" onClick={() => openEditor(t)} data-testid={`template-edit-${t.id}`} title="Edit template">
                              <Pencil size={13} />
                            </Button>
                            <Button variant="ghost" size="sm" className="h-7 px-2 text-rose-600 hover:bg-rose-50" onClick={() => setDelTarget(t)} data-testid={`template-delete-${t.id}`} title="Delete template">
                              <Trash2 size={13} />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {view === 'editor' && (
          <div className="space-y-4">
            <div>
              <Label>Template Name</Label>
              <Input
                autoFocus
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Diamond Buyer (read-only)"
                data-testid="template-name-input"
              />
            </div>
            {/* Matrix + live access preview, side-by-side so the admin can see
                the real-world effect of each toggle while choosing. Stacks on
                narrow screens. */}
            <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_340px] gap-4 items-start">
              <div className="min-w-0">
                <PermissionMatrixEditor
                  permissions={perms}
                  onChange={setPerms}
                  permGroups={permGroups}
                  idPrefix="template"
                />
              </div>
              <div
                className="lg:sticky lg:top-2 self-start rounded-md border border-[var(--divider)] bg-slate-50/60 p-3 lg:max-h-[64vh] lg:overflow-y-auto"
                data-testid="template-editor-preview-panel"
              >
                <div className="text-sm font-medium text-[var(--ink-navy)] inline-flex items-center gap-1.5 mb-2">
                  <Eye size={14} /> Access preview
                </div>
                <PermissionPreview permissions={perms} idPrefix="template-editor-preview" />
              </div>
            </div>
          </div>
        )}

        {view === 'preview' && (
          <div className="space-y-3">
            <div className="text-sm text-slate-600">
              Previewing <span className="font-medium text-[var(--ink-navy)]">{previewTpl?.name}</span>
              {' '}— {(previewTpl?.permissions || []).length} permission
              {(previewTpl?.permissions || []).length === 1 ? '' : 's'}.
            </div>
            <PermissionPreview permissions={previewTpl?.permissions || []} idPrefix="template-preview" />
          </div>
        )}

        <DialogFooter>
          {view === 'list' && (
            <Button variant="outline" onClick={() => onOpenChange(false)}>Close</Button>
          )}
          {view === 'preview' && (
            <>
              <Button variant="outline" onClick={() => { setView('list'); setPreviewTpl(null); }}>
                <ArrowLeft size={14} className="mr-1.5" /> Back
              </Button>
              {previewTpl && (
                <Button className="luxury-button" onClick={() => openEditor(previewTpl)} data-testid="template-preview-edit">
                  <Pencil size={14} className="mr-1.5" /> Edit this template
                </Button>
              )}
            </>
          )}
          {view === 'editor' && (
            <>
              <Button variant="outline" onClick={() => setView('list')} disabled={saving}>
                <ArrowLeft size={14} className="mr-1.5" /> Back
              </Button>
              <Button onClick={save} className="luxury-button" disabled={saving || !(name || '').trim()} data-testid="template-save-button">
                {saving ? 'Saving…' : editing ? 'Save Template' : 'Create Template'}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>

      <AlertDialog open={!!delTarget} onOpenChange={(v) => { if (!v) setDelTarget(null); }}>
        <AlertDialogContent data-testid="template-delete-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete template?</AlertDialogTitle>
            <AlertDialogDescription>
              {delTarget
                ? `"${delTarget.name}" will be removed. Its ${delTarget.linked_user_count ?? 0} linked user(s) keep their current permissions but become unlinked (custom).`
                : ''}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="template-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={remove} className="bg-rose-600 hover:bg-rose-700" data-testid="template-delete-confirm-btn">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Dialog>
  );
}

export default PermissionTemplatesManager;
